import { useMemo } from 'react';

import { IconClipboardList } from '@u21/tabler-icons';
import {
  U21MultiSelect,
  U21MultiSelectProps,
  U21Select,
  U21SelectOptionProps,
  U21SelectProps,
} from 'app/shared/u21-ui/components';

import { QueueSubtypes, QueueType } from 'app/modules/queues/models';

import { QUEUE_TYPE_LABEL } from 'app/modules/queues/constants';

import { useGetAllQueues } from 'app/modules/queues/queries/useGetAllQueues';
import { convertFormattedQueues } from 'app/modules/queues/helpers';
import { useSelector } from 'react-redux';
import { selectHasReadQueuePermission } from 'app/modules/queues/selectors';

// Team-based permissions
import {
  selectAgentTeamIds,
  selectHasEditAllWorkflowsPermission,
} from 'app/modules/session/selectors';
import { selectTeamBasedWorkflowPermissionsEnabled } from 'app/shared/featureFlags/selectors';
import { hasTeamOwnershipAccess } from 'app/shared/utils/teamOwnership';

interface U21QueueSelectSingleProps<TClearable extends boolean> extends Omit<
  U21SelectProps<number, TClearable>,
  'options'
> {
  filterByTeamOwnership?: boolean;
  multi?: false;
  queueType: QueueType;
  queueSubtype?: QueueSubtypes;
}

interface U21QueueSelectMultiProps<TClearable extends boolean> extends Omit<
  U21MultiSelectProps<number, TClearable>,
  'options'
> {
  filterByTeamOwnership?: boolean;
  multi: true;
  queueType: QueueType;
  queueSubtype?: QueueSubtypes;
}

export type U21QueueSelectProps<TClearable extends boolean = true> =
  | U21QueueSelectSingleProps<TClearable>
  | U21QueueSelectMultiProps<TClearable>;

export const U21QueueSelect = <TClearable extends boolean = true>(
  props: U21QueueSelectProps<TClearable>,
) => {
  const {
    filterByTeamOwnership = false,
    onChange,
    multi,
    queueType,
    queueSubtype,
    value,
    ...rest
  } = props;

  const { data } = useGetAllQueues(queueType);

  const hasReadQueuePermission = useSelector((state: RootState) =>
    selectHasReadQueuePermission(state, queueType),
  );

  // Team-based permissions
  const teamBasedPermissionsEnabled =
    useSelector(selectTeamBasedWorkflowPermissionsEnabled) ?? false;
  const hasEditAllWorkflows = useSelector(selectHasEditAllWorkflowsPermission);
  const agentTeamIds = useSelector(selectAgentTeamIds);

  const filteredQueues = useMemo(() => {
    if (!filterByTeamOwnership) {
      return data?.queues;
    }
    return data?.queues.filter((queue) =>
      hasTeamOwnershipAccess(
        queue.owning_team_ids,
        agentTeamIds,
        teamBasedPermissionsEnabled,
        hasEditAllWorkflows,
      ),
    );
  }, [
    agentTeamIds,
    data?.queues,
    filterByTeamOwnership,
    hasEditAllWorkflows,
    teamBasedPermissionsEnabled,
  ]);

  const options = useMemo<U21SelectOptionProps[]>(
    () => convertFormattedQueues(filteredQueues, queueType, queueSubtype),
    [filteredQueues, queueSubtype, queueType],
  );

  if (!hasReadQueuePermission) {
    return null;
  }

  const selectProps = {
    label: QUEUE_TYPE_LABEL[queueType],
    options,
    startIcon: <IconClipboardList />,
  };

  if (multi) {
    return (
      <U21MultiSelect
        {...selectProps}
        {...rest}
        onChange={onChange}
        value={value}
      />
    );
  }
  return (
    <U21Select
      allowInvalidValues
      {...selectProps}
      {...rest}
      onChange={onChange}
      value={value}
    />
  );
};
